import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, Tag } from 'lucide-react';
import BackgroundAnimation from '../components/BackgroundAnimation';
import SEOHead from '../components/SEOHead';
import blogArticles from '../data/blogArticles';

const Blog = () => {
  const blogSchema = {
    "@type": "Blog",
    "@id": "https://americanprimellc.com/blog#blog",
    "mainEntityOfPage": "https://americanprimellc.com/blog",
    "name": "American Prime LLC Blog - AI Automation Insights",
    "description": "Expert insights on AI automation, business process optimization, and digital transformation strategies.",
    "publisher": {
      "@id": "https://americanprimellc.com/#organization"
    },
    "blogPost": blogArticles.map(article => ({
      "@type": "BlogPosting",
      "headline": article.title,
      "description": article.excerpt,
      "datePublished": article.publishedDate,
      "author": {
        "@type": "Organization",
        "name": "American Prime LLC"
      },
      "url": `https://americanprimellc.com/blog/${article.slug}`
    }))
  };

  return (
    <div className="min-h-screen page-transition" style={{ background: 'var(--bg-primary)', position: 'relative' }}>
      <SEOHead 
        title="AI Automation Blog - Expert Insights & Guides"
        description="Explore expert articles on AI automation services, intelligent automation solutions, business process optimization, and digital transformation strategies. Learn how to leverage AI for business growth."
        canonicalPath="/blog"
        schema={blogSchema}
      />
      <BackgroundAnimation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16" style={{ padding: '120px 7.6923% 60px' }}>
        <div className="max-w-7xl mx-auto text-center">
          <div className="body-medium mb-4" style={{ color: 'var(--brand-primary)' }}>
            Knowledge Hub
          </div>
          <h1 className="display-large mb-6" style={{ color: 'var(--text-primary)' }}>
            AI Automation Insights & Guides
          </h1>
          <p className="body-large mx-auto" style={{ color: 'var(--text-secondary)', maxWidth: '700px' }}>
            Expert articles on AI automation services, intelligent automation solutions, and digital transformation strategies to help your business grow.
          </p>
        </div>
      </section>

      {/* Featured Article */}
      <section className="pb-16" style={{ padding: '0 7.6923% 60px' }}>
        <div className="max-w-7xl mx-auto">
          <Link 
            to={`/blog/${blogArticles[0].slug}`}
            className="block rounded-lg overflow-hidden group"
            style={{ 
              background: 'rgba(255, 255, 255, 0.03)', 
              border: '1px solid var(--border-subtle)' 
            }}
          >
            <div className="grid md:grid-cols-2 gap-0">
              <div className="h-64 md:h-auto">
                <img 
                  src={blogArticles[0].image} 
                  alt={blogArticles[0].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center gap-4 mb-4">
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ background: 'rgba(0, 255, 209, 0.1)', color: 'var(--brand-primary)' }}
                  >
                    Featured
                  </span>
                  <span className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-muted)' }}>
                    <Clock size={14} />
                    {blogArticles[0].readTime}
                  </span>
                </div>
                <h2 className="heading-1 mb-4 group-hover:text-[var(--brand-primary)] transition-colors" style={{ color: 'var(--text-primary)' }}>
                  {blogArticles[0].title}
                </h2>
                <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                  {blogArticles[0].excerpt}
                </p>
                <div className="flex items-center gap-2" style={{ color: 'var(--brand-primary)' }}>
                  <span className="font-medium">Read Article</span>
                  <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Article Grid */}
      <section className="pb-24" style={{ padding: '0 7.6923% 100px' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogArticles.slice(1).map((article) => (
              <Link 
                key={article.id}
                to={`/blog/${article.slug}`}
                className="block rounded-lg overflow-hidden group"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.03)', 
                  border: '1px solid var(--border-subtle)' 
                }}
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-3">
                    <span 
                      className="px-2 py-1 rounded text-xs"
                      style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)' }}
                    >
                      {article.category}
                    </span>
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                      <Clock size={12} />
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="heading-3 mb-3 line-clamp-2 group-hover:text-[var(--brand-primary)] transition-colors" style={{ color: 'var(--text-primary)' }}>
                    {article.title}
                  </h3>
                  <p className="body-small mb-4 line-clamp-3" style={{ color: 'var(--text-secondary)' }}>
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--brand-primary)' }}>
                    <span>Read More</span>
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20"
        style={{
          background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.05) 0%, rgba(0, 255, 209, 0.02) 100%)',
          borderTop: '1px solid rgba(0, 255, 209, 0.1)',
          borderBottom: '1px solid rgba(0, 255, 209, 0.1)'
        }}
      >
        <div className="max-w-4xl mx-auto text-center" style={{ padding: '0 7.6923%' }}>
          <h2 className="display-medium mb-4" style={{ color: 'var(--text-primary)' }}>
            Ready to Implement AI Automation?
          </h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            Book a free strategy call to discuss how AI can transform your business operations.
          </p>
          <a
            href="https://calendly.com/americanprimeforyou"
            target="_blank"
            rel="noopener noreferrer"
            className="button-text dark-button-animate inline-flex items-center space-x-2"
            style={{
              background: 'var(--brand-primary)',
              color: '#000000',
              padding: '14px 32px',
              borderRadius: '0px',
              textDecoration: 'none'
            }}
            onClick={() => window.trackCTA && window.trackCTA('Book Strategy Call', 'blog_cta')}
          >
            <span>Book Free Strategy Call</span>
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Blog;
